import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AvatarModule } from "primeng/avatar";
import { AvatarGroupModule } from "primeng/avatargroup";
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { IconFieldModule } from 'primeng/iconfield';
import { PopoverModule } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { AutoCompleteComponent } from '../../../@core/components/ui/autocomplete/autocomplete.component';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { CheckboxComponent } from '../../../@core/components/ui/checkbox';
import { IconComponent } from '../../../@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from '../../../@core/components/ui/menu/menu.component';
import { PopoverComponent } from '../../../@core/components/ui/popover/popover.component';
import { IconSourceEnum } from '../../../@core/components/ui/icon/icon.component';
import { USER_TYPE } from '../../../@core/models/enums';
import { CONTRACT_USER_ROLE, IShareContract, IShareUser, IUser, IWorkspace, REQUEST_ACCESS_STATUS, SHARE_ACCESS, SHARE_FEATURE } from '../../../@core/models/model';
import { ToastService } from '../../../@core/services/toast.service';
import { validateEmail } from '../../../common/utils';
import { IContract } from '../../../contract-management/models/contract-management.model';
import { OrganizationSetupFormService } from '../../../contract-management/services/organization-setup-from.service';
import { RequestAccessService } from '../../../contract-management/services/request-access.service';
import { ShareService } from '../../../contract-management/services/share.service';
import { UserService } from '../../../contract-management/services/user.service';
import { FullNamePipe } from "../../pipes/fullName.pipe";
import { UserAvatarPipe } from '../../pipes/user-avatar-pipe';
import { BadgeComponent } from "../badge/badge.component";
import { ShareAccessDialogComponent } from "./components/share-access-dialog/share-access-dialog.component";
import { TextCasePipe } from "../../pipes/textCase.pipe";
import { AssetUrlPipe } from '../../pipes/asset-url.pipe';

@Component({
        selector: 'app-share-to-users',
        standalone: true,
        imports: [FormsModule, ButtonComponent, CheckboxComponent, SelectModule, PopoverModule, UserAvatarPipe, FormsModule, DialogComponent, IconFieldModule, AutoCompleteComponent, TooltipModule, CommonModule, AvatarGroupModule, AvatarModule, FullNamePipe, UserAvatarPipe, ShareAccessDialogComponent, BadgeComponent, PopoverComponent, TextCasePipe, IconComponent, AssetUrlPipe,
                MenuComponent
        ],
        templateUrl: './share-to-users.component.html',
        styleUrl: './share-to-users.component.scss',
})
export class ShareToUsersComponent implements OnChanges {

        @Input() shareDetails!: { users: IShareUser[], anyone: SHARE_ACCESS, workspaceShare?: boolean, projectShare?: boolean };
        @Input() contract: IContract | null = null
        @Input() projectUsers: boolean = false;
        @Input() isProject: boolean = false;
        @Input() projectId?: string; // input to indicate if this is for a project
        @Input() workspace: IWorkspace | null = null;
        @Input() currentUserRole?: SHARE_ACCESS | SHARE_ACCESS[] | null; // current user's role in the project
        @Input() mode?: 'create' | 'edit'; // mode to check if it's edit project
        @Input() disabled: boolean = false;
        @Input() isDocumentForm: boolean = false;
        @Input() isAnalysis: boolean = false;
        @Input() threadId?: string; // input for general agent sharing
        /** When true: hides invite-by-email and shows workspace access toggle instead. */
        @Input() promptMode: boolean = false;
        @Input() analysisReport?: string;
        @Output() addUserToShare: EventEmitter<Partial<IShareUser>> = new EventEmitter()
        @Output() removeUserFromShare: EventEmitter<Partial<IShareUser>> = new EventEmitter()
        @Output() updateSharedUser: EventEmitter<IShareUser> = new EventEmitter()
        @Output() changeAnyoneAccess: EventEmitter<SHARE_ACCESS> = new EventEmitter()
        @Output() refreshShareDetails: EventEmitter<void> = new EventEmitter()

        @Output() changeSharedWithWorkspace: EventEmitter<boolean> = new EventEmitter()
        @Output() changeSharedWithProject: EventEmitter<boolean> = new EventEmitter()
        @Output() needAccessUsers: EventEmitter<{ user: Partial<IUser>, shareUser: Partial<IShareUser>, hasAccess: boolean }[]> = new EventEmitter()

        needAccess: { user: Partial<IUser>, shareUser: Partial<IShareUser>, hasAccess: boolean }[] = []

        shareDialog: boolean = false

        users: IUser[] = []
        selectedShareUser: { user: IUser, role: string }[] = [];
        SHARE_ACCESS = SHARE_ACCESS
        SHARE_FEATURE = SHARE_FEATURE
        docsShareOptions: {
                label: string,
                value: SHARE_ACCESS,
                separator?: boolean
        }[] = [];
        roles: { [key: string]: string } = {
                'View': 'Viewer',
                'Edit': 'Editor',
                'Comment': 'Commenter',
                'Restricted': 'Restricted',
                'Admin': 'Admin',
                'Owner': 'Owner',
                'Manager': 'Manager',
                'Approver': 'Approver',
                'Signatory': 'Signatory'
        }
        anyoneOptions: {
                label: string,
                value: SHARE_ACCESS,
                separator?: boolean
        }[] = [];
        shareUrl = ''
        errorMessage: string = ''

        openRoleMenuIndex: number | null = null
        isSharedWithWorkspace: boolean = false
        isSharedWithProject: boolean = false

        workspaceUsers: IUser[] = []
        filteredWorkspaceUsers: IUser[] = []
        workspaceUsersLength: number = 0

        orgAdmins: IUser[] = []
        filteredOrgAdmins: IUser[] = []
        orgAdminsLength: number = 0

        UsersInProject: IUser[] = []
        filteredUsersInProject: IUser[] = []
        usersInProjectLength: number = 0

        resource: string = ''
        feature: SHARE_FEATURE = SHARE_FEATURE.CONTRACT
        organizationName: string = ''

        viewAllUsersDialog: boolean = false
        viewAllDialogUsers: IUser[] = []
        viewAllDialogTitle: string = ''

        externalUserConfirmDialog: boolean = false;
        externalUserConfirmMessage: string = '';
        externalUserConfirmAccept: Function | null = null;

        onExternalUserConfirmAccept() {
                this.externalUserConfirmDialog = false;
                if (this.externalUserConfirmAccept) {
                        this.externalUserConfirmAccept();
                        this.externalUserConfirmAccept = null;
                }
        }

        constructor(
                private userService: UserService,
                private toast: ToastService,
                private shareService: ShareService,
                private organizationService: OrganizationSetupFormService,
                private requestAccessService: RequestAccessService,
        ) {
        }
        getApproverLabel() {
                return this.workspace ? 'Request' : 'Invite'
        }

        ngOnInit() {
                this.getDocOptions();
                this.getUsers()
                this.getShareUrl();
                this.getWorkspaceUsers()
                this.getOrgAdmins()
                this.getProjectUsers()
                this.setResourceAndFeature()
                this.setAnyoneOptions();
        }

        ngOnChanges(changes: SimpleChanges): void {
                if (changes['projectId']) {
                        if (this.projectId) {
                                this.getProjectUsers();
                        } else {
                                this.UsersInProject = [];
                                this.filteredUsersInProject = [];
                                this.usersInProjectLength = 0;
                        }
                }
        }

        setAnyoneOptions() {
                this.anyoneOptions = [
                        { label: this.getRoleLabelWithFeature(SHARE_ACCESS.VIEW), value: SHARE_ACCESS.VIEW },
                        { label: this.getRoleLabelWithFeature(SHARE_ACCESS.COMMENT), value: SHARE_ACCESS.COMMENT },
                        { label: this.getRoleLabelWithFeature(SHARE_ACCESS.EDIT), value: SHARE_ACCESS.EDIT },
                        { label: this.getRoleLabelWithFeature(SHARE_ACCESS.RESTRICTED), value: SHARE_ACCESS.RESTRICTED }
                ];
        }

        getDocOptions() {
                if (this.analysisReport || this.isAnalysis) {
                        this.docsShareOptions = [
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.VIEW), value: SHARE_ACCESS.VIEW },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.EDIT), value: SHARE_ACCESS.EDIT },
                        ];
                } else if (this.threadId) {
                        this.docsShareOptions = [
                                { label: this.roles[SHARE_ACCESS.VIEW], value: SHARE_ACCESS.VIEW },
                                // { label: this.roles[SHARE_ACCESS.EDIT], value: SHARE_ACCESS.EDIT },
                        ];
                } else if (this.workspace) {
                        this.docsShareOptions = [
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.ADMIN), value: SHARE_ACCESS.ADMIN },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.EDIT), value: SHARE_ACCESS.EDIT },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.COMMENT), value: SHARE_ACCESS.COMMENT },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.VIEW), value: SHARE_ACCESS.VIEW }
                        ];
                } else if (this.contract || this.isDocumentForm) {
                        this.docsShareOptions = [
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.EDIT), value: SHARE_ACCESS.EDIT },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.VIEW), value: SHARE_ACCESS.VIEW },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.COMMENT), value: SHARE_ACCESS.COMMENT }
                        ];
                } else if (this.isProject || this.projectId) {
                        this.docsShareOptions = [
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.ADMIN), value: SHARE_ACCESS.ADMIN },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.EDIT), value: SHARE_ACCESS.EDIT },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.VIEW), value: SHARE_ACCESS.VIEW },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.COMMENT), value: SHARE_ACCESS.COMMENT }
                        ];
                } else {
                        this.docsShareOptions = [
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.VIEW), value: SHARE_ACCESS.VIEW },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.COMMENT), value: SHARE_ACCESS.COMMENT },
                                { label: this.getRoleLabelWithFeature(SHARE_ACCESS.EDIT), value: SHARE_ACCESS.EDIT }
                        ];
                }
        }

        getAnyoneLabel(shareDetails: Partial<IShareContract>) {
                let defaultRole = this.contract ? SHARE_ACCESS.VIEW : SHARE_ACCESS.RESTRICTED;
                let role = shareDetails?.anyone ?? defaultRole;
                return this.getRoleLabelWithFeature(role);
        }

        getRoleLabel(role: SHARE_ACCESS | SHARE_ACCESS[] | undefined) {
                if (!role) return ''
                // Handle array of roles - filter out Approver and Signatory for display
                if (Array.isArray(role)) {
                        const filteredRoles = role.filter(r => r !== SHARE_ACCESS.APPROVER && r !== SHARE_ACCESS.SIGNATORY);
                        if (filteredRoles.length === 0) return '';
                        if (filteredRoles.length === 1) {
                                return this.getRoleLabelWithFeature(filteredRoles[0]);
                        }
                        // If multiple roles, return the first one with feature label
                        return this.getRoleLabelWithFeature(filteredRoles[0]);
                }
                return this.getRoleLabelWithFeature(role)
        }

        // Get users with specific role
        getUsersByRole(roleName: SHARE_ACCESS): IShareUser[] {
                return this.shareDetails.users.filter((user: IShareUser) => {
                        if (Array.isArray(user.role)) {
                                return user.role.includes(roleName)
                        }
                        return user.role === roleName
                })
        }

        // Get approvers
        getApprovers(): IShareUser[] {
                return this.getUsersByRole(SHARE_ACCESS.APPROVER)
        }

        // Get signatories
        getSignatories(): IShareUser[] {
                return this.getUsersByRole(SHARE_ACCESS.SIGNATORY)
        }

        // Get users excluding Approvers and Signatories
        getUsersExcludingApproversAndSignatories(): IShareUser[] {
                return this.shareDetails.users.filter((user: IShareUser) => {
                        if (Array.isArray(user.role)) {
                                // Include user only if they don't have ONLY Approver or Signatory roles
                                const hasOtherRoles = user.role.some(r => r !== SHARE_ACCESS.APPROVER && r !== SHARE_ACCESS.SIGNATORY);
                                return hasOtherRoles;
                        }
                        // Include all non-array roles (backward compatibility)
                        return true;
                })
        }

        isDisabled(item: IShareUser) {
                if (this.isProject && this.mode === 'edit') {
                        return !this.canEditUserRoles() || this.hasRole(item, 'Owner');
                }
                return !(this.canEditUserRoles() && !this.hasRole(item, 'Owner'));
        }

        // Check if user has specific role
        hasRole(user: IShareUser, roleName: string): boolean {
                if (Array.isArray(user.role)) {
                        return user.role.includes(roleName as SHARE_ACCESS)
                }
                return user.role === roleName
        }

        // Check if role is an array
        isRoleArray(role: any): boolean {
                return Array.isArray(role)
        }

        // Check if role array includes a specific value
        roleArrayIncludes(role: any, value: string): boolean {
                if (Array.isArray(role)) {
                        return role.includes(value)
                }
                return role === value
        }

        getRoleLabelWithFeature(role: SHARE_ACCESS): string {
                const baseLabel = this.roles[role] || role;
                let featurePrefix = '';
                if (this.analysisReport || this.isAnalysis) {
                        featurePrefix = 'Analysis ';
                } else if (this.threadId) {
                        featurePrefix = 'Thread ';
                } else if (this.workspace) {
                        featurePrefix = 'Workspace ';
                } else if (this.contract || this.isDocumentForm) {
                        featurePrefix = 'Document ';
                } else if (this.isProject || this.projectId) {
                        featurePrefix = 'Project ';
                }
                return featurePrefix + baseLabel.toLowerCase();
        }

        getShareUrl() {
                if (this.contract) {
                        this.shareUrl = `${window.location.origin}/edit-document/share?documentId=${this.contract._id}`
                }
        }

        getUsers() {
                if (this.projectUsers && this.projectId) {
                        this.userService.getProjectUsers(this.projectId).subscribe(res => {
                                this.users = res.data
                        })
                } else {
                        this.userService.getUsers().subscribe(res => {
                                this.users = res.data.users
                        })
                }
        }

        getWorkspaceUsers() {
                this.userService.getWorkspaceUsers().subscribe(res => {
                        this.workspaceUsers = res.data
                        this.workspaceUsersLength = res.data.length
                        this.filteredWorkspaceUsers = res.data.slice(0, 5)

                })
        }

        getOrgAdmins() {
                this.userService.getAdmins().subscribe(res => {
                        this.orgAdmins = res.data
                        this.orgAdminsLength = res.data.length
                        this.filteredOrgAdmins = res.data.slice(0, 5)
                })
        }

        getProjectUsers() {
                if (!this.projectId) return
                this.userService.getProjectUsers(this.projectId).subscribe(res => {
                        this.UsersInProject = res.data
                        this.usersInProjectLength = res.data.length
                        this.filteredUsersInProject = res.data.slice(0, 5)
                })
        }

        openViewAllDialog(type: 'orgAdmins' | 'workspace' | 'project') {
                switch (type) {
                        case 'orgAdmins':
                                this.viewAllDialogTitle = 'Organization admins & account owner';
                                this.viewAllDialogUsers = this.orgAdmins;
                                break;
                        case 'workspace':
                                this.viewAllDialogTitle = 'Workspace users';
                                this.viewAllDialogUsers = this.workspaceUsers;
                                break;
                        case 'project':
                                this.viewAllDialogTitle = 'Project users';
                                this.viewAllDialogUsers = this.UsersInProject;
                                break;
                        default:
                                this.viewAllDialogTitle = 'Users';
                                this.viewAllDialogUsers = [];
                                break;
                }
                this.viewAllUsersDialog = true;
        }

        isUserUnverified(item: any): boolean {
                return item && item.user && typeof item.user !== 'string' && item.user.verified === false;
        }

        shareToUser($event: any) {
                this.selectedShareUser = []
                const users = $event.value
                users.map((user: any) => {
                        this.selectedShareUser.push({ user: user, role: CONTRACT_USER_ROLE.VIEWER })
                })
        }

        Cancel(op: any) {
                op.hide();
        }

        openShareDialog() {
                this.shareDialog = true
        }

        onCloseShareDialog() {
                this.shareDialog = false
        }

        toggleShareDialog() {
                this.shareDialog = !this.shareDialog
        }

        copyShareUrlToClipboard() {
                navigator.clipboard.writeText(this.shareUrl).then(() => {
                        this.toast.success('Copied to clipboard');
                }).catch(err => {
                        console.error('Failed to copy text: ', err);
                });
        }

        autoCompleteUsers: any[] = []
        autoCompleteValue: string = '';

        autoCompleteUsersMethod(event: any) {
                // For threads, only show workspace users (internal users only)
                let filteredUsers = this.users;
                if (this.threadId) {
                        // Only workspace users are already fetched in getUsers() for threads
                        filteredUsers = this.users;
                }
                this.autoCompleteUsers = filteredUsers.filter((user: any) => {
                        const fullName = user?.fullName ?? (user.firstName + ' ' + user.lastName)
                        return (fullName.toLowerCase().includes(event.query.toLowerCase()) || user.email.toLowerCase().includes(event.query.toLowerCase()))
                })
                //Remove users that are already in the shareDetails (except those who are ONLY Approver/Signatory - they can be added with other roles)
                this.autoCompleteUsers = this.autoCompleteUsers.filter((user: any) => {
                        const existingUser = this.shareDetails.users.find((shareUser: IShareUser) => shareUser.email === user.email);
                        if (!existingUser) return true; // User not in shareDetails, show them

                        // If user exists, check if they have ONLY Approver or Signatory roles
                        if (Array.isArray(existingUser.role)) {
                                const hasOnlyApproverSignatory = existingUser.role.every(role =>
                                        role === SHARE_ACCESS.APPROVER || role === SHARE_ACCESS.SIGNATORY
                                );
                                return hasOnlyApproverSignatory; // Show if they only have Approver/Signatory
                        }

                        // Single role - if it's Approver or Signatory, show them; otherwise hide
                        return existingUser.role === SHARE_ACCESS.APPROVER || existingUser.role === SHARE_ACCESS.SIGNATORY;
                })
        }

        shareAccessDialog: boolean = false
        needAccessUser: Partial<IUser> = {}

        setResourceAndFeature() {
                if (this.promptMode && this.threadId) {
                        this.resource = this.threadId
                        this.feature = SHARE_FEATURE.PROMPT
                } else if (this.threadId) {
                        this.resource = this.threadId
                        this.feature = SHARE_FEATURE.GENERAL_AGENT
                } else if (this.contract && this.projectId) {
                        this.resource = this.contract._id
                        this.feature = SHARE_FEATURE.CONTRACT
                } else if (this.isProject || this.projectId) {
                        this.resource = this.projectId ?? ''
                        this.feature = SHARE_FEATURE.PROJECT
                }
                else if (this.workspace) {
                        this.resource = this.workspace?._id ?? ''
                        this.feature = SHARE_FEATURE.WORKSPACE
                }
                return { resource: this.resource, feature: this.feature }
        }

        closeShareAccessDialog(event: boolean) {
                this.shareAccessDialog = false
                this.needAccessUser = {}
                this.autoCompleteValue = ''
        }

        userAccessChanged(event: { user: Partial<IUser>, shareUser: Partial<IShareUser>, hasAccess: boolean }) {
                this.getWorkspaceUsers()
                this.needAccess.push(event)
                const user = event.user
                const hasAccess = event.hasAccess
                let shareUser: Partial<IShareUser> = {
                        email: event.shareUser?.email ?? this.autoCompleteValue,
                        role: event.shareUser?.role ?? SHARE_ACCESS.VIEW,
                }
                this.shareDetails.users.push({
                        name: user?.fullName ?? '',
                        user: user?._id ?? '',
                        email: user?.email ?? this.autoCompleteValue,
                        type: USER_TYPE.INTERNAL,
                        role: event.shareUser?.role ?? SHARE_ACCESS.VIEW,
                })
                this.addUser(shareUser)
                this.autoCompleteValue = ''
                this.needAccessUsers.emit(this.needAccess)
                this.shareAccessDialog = false
                this.needAccessUser = {}
        }

        async externalUserCheck(email: string, accept: Function) {
                if (!this.organizationName) {
                        await new Promise((resolve) => this.organizationService.fetchOrganizationSetupData().subscribe((res) => {
                                this.organizationName = res.data.name
                                resolve(true)
                        }))
                }
                const emailId = email.trim()
                const user = this.users.find((user) => user.email === emailId)
                if (user) {
                        accept()
                        return
                }
                this.externalUserConfirmMessage = `The email address ${email} belongs to an external domain outside ${this.organizationName}. Please confirm you wish to proceed with sharing this item externally`;
                this.externalUserConfirmAccept = accept;
                this.externalUserConfirmDialog = true;
        }

        addShareUser(event: { value: IUser } | null) {

                if (this.shareDetails.users.some((user: IShareUser) => user.email === this.autoCompleteValue)) {
                        this.toast.warn('User already added');
                        return;
                }
                const user = event?.value;
                const email = user?.email ?? this.autoCompleteValue;
                this.autoCompleteValue = '';

                if (!email) return;

                // For threads, only allow system users (must select from autocomplete, no external users)
                if (this.threadId && !user) {
                        this.toast.warn('Please select a user from the list');
                        return;
                }

                const addUser = () => {
                        const user = this.users.find((user) => user.email === email)
                        const shareUser: Partial<IShareUser> = {
                                email,
                                role: [SHARE_ACCESS.VIEW],
                        };

                        const shareUserDetails = {
                                name: user == null ? email.split('@')[0] : user?.fullName ?? [user?.firstName, user?.lastName].filter(Boolean).join(' '),
                                user,
                                email,
                                type: user == null ? USER_TYPE.EXTERNAL : USER_TYPE.INTERNAL,
                                role: [SHARE_ACCESS.VIEW],
                                status: user == null ? REQUEST_ACCESS_STATUS.PENDING : undefined,
                        };

                        const addUserToShareList = () => {
                                this.shareDetails.users.push(shareUserDetails);
                                this.addUser(shareUser);
                        };

                        if (user) {
                                this.shareService.isShareUserHaveAccess(user).subscribe(res => {
                                        if (res.data.isOrgAdmin && !this.threadId) {
                                                this.errorMessage = 'This user is an Organization admin / account owner and already has full access.';
                                                setTimeout(() => {
                                                        this.errorMessage = '';
                                                }, 5000);
                                                return;
                                        }
                                        if (!this.workspace) {
                                                if (!res.data.hasAccess) {
                                                        this.shareAccessDialog = true;
                                                        this.needAccessUser = user;
                                                        return;
                                                }
                                        }
                                        addUserToShareList();
                                });
                        } else {
                                addUserToShareList();
                        }
                }
                // For threads, don't check for external users
                if (this.threadId) {
                        addUser();
                } else if (!this.workspace)
                        this.externalUserCheck(email, addUser)
                else
                        addUser()
        }


        addUser(shareUser: Partial<IShareUser>) {
                this.addUserToShare.emit(shareUser)
        }

        validApproverEmail() {
                return validateEmail(this.autoCompleteValue)
        }

        confirmRemoveUserDialog: boolean = false
        selectedUserToRemove: IShareUser | null = null

        confirmRemoveUser() {
                if (!this.selectedUserToRemove) return
                this.confirmRemoveUserDialog = false;

                const removedUserEmail = this.selectedUserToRemove.email;

                // Remove from needAccess array
                if (this.needAccess.length > 0) {
                        this.needAccess = this.needAccess.filter(user => user.user.email !== removedUserEmail);
                }

                // Emit the removal event - parent will handle backend call
                this.removeUserFromShare.emit(this.selectedUserToRemove);
                this.needAccessUsers.emit(this.needAccess);

                // Emit refresh event to tell parent to refetch share details from backend
                this.refreshShareDetails.emit();

                this.selectedUserToRemove = null
        }

        removeSharedUser(user: IShareUser, overlayPanel?: any) {
                this.selectedUserToRemove = user
                this.confirmRemoveUserDialog = true
                if (overlayPanel) {
                        overlayPanel.hide()
                }
        }

        updateUser(option: any, index: number, overlayPanel?: any) {
                const user = this.shareDetails.users[index]
                if (Array.isArray(user['role'])) {
                        const secondaryRoles = user['role'].filter((role: SHARE_ACCESS) =>
                                role === SHARE_ACCESS.APPROVER || role === SHARE_ACCESS.SIGNATORY
                        )
                        user['role'] = [option.value, ...secondaryRoles]
                } else {
                        user['role'] = option.value
                }
                this.updateSharedUser.emit(user)
                if (overlayPanel) {
                        overlayPanel.hide()
                }
        }

        getRoleIcon(value: SHARE_ACCESS | SHARE_ACCESS[]): string | undefined {
                if (value === SHARE_ACCESS.ADMIN) return 'shield'
                if (value === SHARE_ACCESS.VIEW) return 'eye'
                if (value === SHARE_ACCESS.EDIT) return 'file-pen-line'
                if (value === SHARE_ACCESS.COMMENT) return 'message-square-more'
                return undefined
        }

        getRoleMenuItems(idx: number): CustomMenuItem[] {
                const item = this.shareDetails.users[idx]
                const options: CustomMenuItem[] = this.docsShareOptions.map(opt => ({
                        icon: this.getRoleIcon(opt.value),
                        iconSource: IconSourceEnum.LUCIDE,
                        label: opt.label,
                        value: opt.value,
                }))
                if (!this.isDisabled(item)) {
                        options.push({ icon: 'trash', iconSource: IconSourceEnum.LUCIDE, label: 'Remove', value: '__remove__', class: 'text-danger' })
                }
                return options
        }

        getSelectedRoleMenuValues(item: IShareUser): CustomMenuItem[] {
                const primary = Array.isArray(item.role)
                        ? item.role.filter((r: SHARE_ACCESS) => r !== SHARE_ACCESS.APPROVER && r !== SHARE_ACCESS.SIGNATORY)[0]
                        : item.role
                if (!primary) return []
                const opt = this.docsShareOptions.find(o => o.value === primary)
                return opt ? [{ label: opt.label, value: opt.value }] : []
        }

        onRoleMenuVisibleChange(open: boolean, idx: number) {
                this.openRoleMenuIndex = open ? idx : null
        }

        onRoleMenuItemSelected(menuItem: CustomMenuItem, idx: number) {
                this.openRoleMenuIndex = null
                if (menuItem.value === '__remove__') {
                        this.removeSharedUser(this.shareDetails.users[idx])
                } else {
                        this.updateUser({ label: menuItem.label, value: menuItem.value }, idx)
                }
        }

        updateAnyoneAccess(anyoneAccess: SHARE_ACCESS) {
                this.shareDetails.anyone = anyoneAccess
                this.changeAnyoneAccess.emit(anyoneAccess)
        }

        canEditUserRoles(): boolean {
                if (this.mode === 'create') return true;
                if (this.isProject && this.mode === 'edit') {
                        const roles = Array.isArray(this.currentUserRole) ? this.currentUserRole : (this.currentUserRole ? [this.currentUserRole] : []);
                        return roles.includes(SHARE_ACCESS.OWNER) || roles.includes(SHARE_ACCESS.ADMIN);
                }
                return true;
        }

        resendInvite(email: string) {
                this.shareDetails.users = this.shareDetails.users.map((user: IShareUser) => {
                        if (user.email === email && user.type === USER_TYPE.EXTERNAL) {
                                return { ...user, status: REQUEST_ACCESS_STATUS.PENDING };
                        }
                        return user;
                })
                this.updateSharedUser.emit(this.shareDetails.users.find((user: IShareUser) => user.email === email && user.type === USER_TYPE.EXTERNAL) as IShareUser);

        }

        isAccessLevelExpanded: boolean = false;
        isApproversExpanded: boolean = false;
        isSignatoriesExpanded: boolean = false;
        isPeopleWithAccessExpanded: boolean = true;

        toggleAccessLevel() {
                this.isAccessLevelExpanded = !this.isAccessLevelExpanded;
        }

        toggleApprovers() {
                this.isApproversExpanded = !this.isApproversExpanded;
        }

        toggleSignatories() {
                this.isSignatoriesExpanded = !this.isSignatoriesExpanded;
        }

        togglePeopleWithAccess() {
                this.isPeopleWithAccessExpanded = !this.isPeopleWithAccessExpanded;
        }
}
